import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { useProject } from '../context/ProjectContext';
import { Button } from './ui/button';
import { 
  Download, 
  ZoomIn, 
  ZoomOut, 
  Maximize2,
  Printer,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Hand,
  MousePointer2,
  Search
} from 'lucide-react';

// ================================================================
// KONFIGURATION - DIN A4 Querformat nach DIN/VDE
// ================================================================
const PAGE = {
  width: 1052.362,
  height: 744.094,
  marginLeft: 35.433,
  marginRight: 35.433,
  marginTop: 53.149,
  drawingLeft: 35.433,
  drawingRight: 1016.929,
  drawingTop: 70.866,
  drawingBottom: 637.795,
  columnCount: 8,
  columnWidth: 122.687,
  titleBlockY: 637.795,
  titleBlockHeight: 70.866
};

const COLORS = {
  frame: '#000000',
  text: '#000000',
  L1: '#000000',
  L2: '#000000',
  L3: '#000000',
  N: '#0000FF',
  PE: '#008000',
  wire: '#000000'
};

// Layout-Konstanten für vertikale Positionen
const Y = {
  busbars: 95,           // Hauptsammelschienen L1, L2, L3, N
  busbarSpacing: 12,     // Abstand zwischen Sammelschienen
  firstRow: 150,         // Erste Baureihe (SLS, RCD, etc.)
  rcdOutput: 240,        // RCD-Ausgangsschienen
  rcdOutputSpacing: 12,  // Abstand zwischen RCD-Ausgangsschienen
  secondRow: 300,        // Zweite Baureihe (LS unter RCD)
  peBusbar: 520,         // PE-Schiene - weiter unten, immer mit HES verbunden
  terminals: 540,        // Klemmenleiste
  maxDrawing: 580        // Maximale Y-Position
};

// ================================================================
// HAUPT-KOMPONENTE
// ================================================================
const StromlaufplanView = ({ compact = false, onSelectNode }) => {
  const { currentProject, setSelectedNode, findNodeById } = useProject();
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [zoom, setZoom] = useState(1); // Standard: 100%
  const [currentPage, setCurrentPage] = useState(1);
  const [firmendaten, setFirmendaten] = useState({});
  
  // Werkzeug-Modus: 'pan' (Hand zum Schieben), 'zoom' (Lupe), 'select' (Finger zum Auswählen)
  const [toolMode, setToolMode] = useState('select');
  
  // Pan-Funktionalität
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [scrollStart, setScrollStart] = useState({ x: 0, y: 0 });

  // Firmendaten aus localStorage laden
  useEffect(() => {
    const loadFirmendaten = () => {
      try {
        const settings = JSON.parse(localStorage.getItem('accountSettings') || '{}');
        setFirmendaten(settings.firmendaten || {});
      } catch (e) {
        console.error('Fehler beim Laden der Firmendaten:', e);
      }
    };
    
    loadFirmendaten();
    
    // Auf Änderungen lauschen
    const handleStorageChange = () => loadFirmendaten();
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('firmendatenUpdated', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('firmendatenUpdated', handleStorageChange);
    };
  }, []);

  // ================================================================
  // PAN-HANDLER (Hand-Werkzeug)
  // ================================================================
  const handleMouseDown = useCallback((e) => {
    if (toolMode === 'pan') {
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY });
      if (containerRef.current) {
        setScrollStart({ 
          x: containerRef.current.scrollLeft, 
          y: containerRef.current.scrollTop 
        });
      }
      e.preventDefault();
    }
  }, [toolMode]);

  const handleMouseMove = useCallback((e) => {
    if (isPanning && containerRef.current) {
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      containerRef.current.scrollLeft = scrollStart.x - dx;
      containerRef.current.scrollTop = scrollStart.y - dy;
    }
  }, [isPanning, panStart, scrollStart]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  // ================================================================
  // KLICK-HANDLER (Auswahl-Werkzeug)
  // ================================================================
  const handleSvgClick = useCallback((e) => {
    if (toolMode !== 'select') return;
    
    // Finde das geklickte Element mit data-node-id
    let target = e.target;
    while (target && target !== e.currentTarget) {
      const nodeId = target.getAttribute('data-node-id');
      if (nodeId) {
        // Knoten im Baum auswählen
        const node = findNodeById(nodeId);
        if (node) {
          setSelectedNode(node);
          if (onSelectNode) onSelectNode(node);
        }
        return;
      }
      target = target.parentElement;
    }
  }, [toolMode, findNodeById, setSelectedNode, onSelectNode]);

  // Zoom mit Mausrad im Zoom-Modus
  const handleWheel = useCallback((e) => {
    if (toolMode === 'zoom') {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom(z => Math.max(0.3, Math.min(3, z + delta)));
    }
  }, [toolMode]);

  // ================================================================
  // DATEN-EXTRAKTION AUS PROJEKTBAUM
  // Rekursiv alle Komponenten aus dem Baum sammeln
  // ================================================================
  const projectData = useMemo(() => {
    if (!currentProject?.nodes) {
      return { einspeisung: null, components: [], pages: [] };
    }

    const result = {
      einspeisung: null,
      slsList: [],           // Mehrere SLS möglich
      zaehler: null,
      ueberspannungsschutz: null,
      klemme: null,
      rcds: [],
      rcbos: [],
      standaloneLS: []
    };

    // Rekursive Funktion zum Durchsuchen des Baums
    const traverse = (nodes, parentPath = []) => {
      nodes.forEach(node => {
        const path = [...parentPath, node];
        
        switch (node.type) {
          case 'einspeisung':
          case 'zuleitung':
            result.einspeisung = node;
            break;
          case 'sls':
            result.slsList.push(node);  // Sammle alle SLS
            break;
          case 'zaehler':
            result.zaehler = node;
            break;
          case 'ueberspannungsschutz':
            result.ueberspannungsschutz = node;
            break;
          case 'klemme':
            result.klemme = node;
            break;
          case 'rcd':
            // RCD mit seinen Kindern (LS)
            const rcdChildren = (node.children || []).filter(c => 
              c.type === 'ls' || c.type === 'rcbo'
            );
            result.rcds.push({ ...node, lsChildren: rcdChildren });
            break;
          case 'rcbo':
            // RCBO ohne RCD-Parent
            if (!parentPath.some(p => p.type === 'rcd')) {
              result.rcbos.push(node);
            }
            break;
          case 'ls':
            // LS ohne RCD-Parent (direkt an Sammelschienen)
            if (!parentPath.some(p => p.type === 'rcd')) {
              result.standaloneLS.push(node);
            }
            break;
          default:
            break;
        }
        
        // Kinder durchsuchen (außer bei RCDs, deren Kinder schon erfasst sind)
        if (node.children && node.type !== 'rcd') {
          traverse(node.children, path);
        }
      });
    };

    traverse(currentProject.nodes);

    // Seiten aufteilen - inline definiert um Hoisting-Problem zu vermeiden
    const buildPagesFromData = (data) => {
      const pages = [];
      const MAX_COLUMNS = 7;

      // Seite 1: Deckblatt (leer - wird separat gerendert)
      pages.push({ type: 'cover' });

      // Seite 2: Einspeisung
      pages.push({ 
        type: 'einspeisung',
        einspeisung: data.einspeisung,
        slsList: data.slsList,
        zaehler: data.zaehler,
        ueberspannungsschutz: data.ueberspannungsschutz,
        klemme: data.klemme
      });

      // Seite 3+: RCDs und RCBOs
      let currentPageItems = [];
      let currentColumnCount = 0;

      // Zuerst RCDs hinzufügen
      data.rcds.forEach(rcd => {
        const lsCount = rcd.lsChildren?.length || 0;
        const columnsNeeded = 1 + lsCount; // RCD + seine LS

        if (columnsNeeded > MAX_COLUMNS) {
          // RCD muss auf mehrere Seiten aufgeteilt werden
          let lsIndex = 0;
          let partNum = 1;
          const totalParts = Math.ceil(lsCount / (MAX_COLUMNS - 1));

          while (lsIndex < lsCount) {
            // Aktuelle Seite abschließen wenn nicht leer
            if (currentPageItems.length > 0) {
              pages.push({ type: 'circuits', items: currentPageItems });
              currentPageItems = [];
              currentColumnCount = 0;
            }

            const lsSlice = rcd.lsChildren.slice(lsIndex, lsIndex + MAX_COLUMNS - 1);
            currentPageItems.push({
              type: 'rcd_group',
              rcd: rcd,
              children: lsSlice,
              isContinuation: partNum > 1,
              partNum,
              totalParts
            });
            currentColumnCount = 1 + lsSlice.length;

            // Seite abschließen
            pages.push({ type: 'circuits', items: currentPageItems });
            currentPageItems = [];
            currentColumnCount = 0;

            lsIndex += lsSlice.length;
            partNum++;
          }
        } else {
          // RCD passt komplett
          if (currentColumnCount + columnsNeeded > MAX_COLUMNS) {
            pages.push({ type: 'circuits', items: currentPageItems });
            currentPageItems = [];
            currentColumnCount = 0;
          }

          currentPageItems.push({
            type: 'rcd_group',
            rcd: rcd,
            children: rcd.lsChildren || []
          });
          currentColumnCount += columnsNeeded;
        }
      });

      // RCBOs hinzufügen (ohne RCD)
      data.rcbos.forEach(rcbo => {
        if (currentColumnCount >= MAX_COLUMNS) {
          pages.push({ type: 'circuits', items: currentPageItems });
          currentPageItems = [];
          currentColumnCount = 0;
        }
        currentPageItems.push({ type: 'rcbo', data: rcbo });
        currentColumnCount++;
      });

      // Standalone LS hinzufügen
      data.standaloneLS.forEach(ls => {
        if (currentColumnCount >= MAX_COLUMNS) {
          pages.push({ type: 'circuits', items: currentPageItems });
          currentPageItems = [];
          currentColumnCount = 0;
        }
        currentPageItems.push({ type: 'standalone_ls', data: ls });
        currentColumnCount++;
      });

      // Letzte Seite hinzufügen
      if (currentPageItems.length > 0) {
        pages.push({ type: 'circuits', items: currentPageItems });
      }

      return pages;
    };

    const pages = buildPagesFromData(result);
    
    return { ...result, pages };
  }, [currentProject]);

  const totalPages = projectData.pages.length;

  // ================================================================
  // HILFSFUNKTIONEN
  // ================================================================
  const getColumnX = (col) => PAGE.drawingLeft + col * PAGE.columnWidth + PAGE.columnWidth / 2;

  const wrapText = (text, maxChars = 15) => {
    if (!text || text.length <= maxChars) return [text || ''];
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';
    words.forEach(word => {
      if ((currentLine + ' ' + word).trim().length <= maxChars) {
        currentLine = (currentLine + ' ' + word).trim();
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    if (currentLine) lines.push(currentLine);
    return lines.slice(0, 3);
  };

  const formatKabeltyp = (kabelData) => {
    if (!kabelData) return '';
    const typ = kabelData.kabeltyp || 'NYM-J';
    const anzahl = kabelData.anzahlAdern || 3;
    const quer = kabelData.querschnitt || '2,5';
    return `${typ} ${anzahl}x${quer}mm²`;
  };

  const getKabel = (node) => {
    if (!node.children) return [];
    return node.children.filter(c => c.type === 'kabel');
  };

  // ================================================================
  // SVG RENDER FUNKTIONEN
  // ================================================================

  // Seitenrahmen
  const renderPageFrame = () => `
    <rect x="${PAGE.marginLeft}" y="${PAGE.marginTop}" 
          width="${PAGE.width - 2*PAGE.marginLeft}" height="${PAGE.drawingBottom - PAGE.marginTop + PAGE.titleBlockHeight}"
          fill="none" stroke="${COLORS.frame}" stroke-width="0.7"/>
    ${Array.from({length: 9}, (_, i) => {
      const x = PAGE.drawingLeft + i * PAGE.columnWidth;
      return `<line x1="${x}" y1="${PAGE.marginTop}" x2="${x}" y2="${PAGE.marginTop + 17}" stroke="${COLORS.frame}" stroke-width="0.5"/>`;
    }).join('')}
    ${Array.from({length: 8}, (_, i) => {
      const x = getColumnX(i);
      return `<text x="${x}" y="${PAGE.marginTop + 12}" text-anchor="middle" font-size="14" font-family="Arial">${i + 1}</text>`;
    }).join('')}
    <line x1="${PAGE.drawingLeft}" y1="${PAGE.marginTop + 17}" x2="${PAGE.drawingRight}" y2="${PAGE.marginTop + 17}" stroke="${COLORS.frame}" stroke-width="0.7"/>
  `;

  // Schriftfeld
  const renderTitleBlock = () => {
    const y = PAGE.titleBlockY;
    const p = currentProject || {};
    return `
      <line x1="${PAGE.drawingLeft}" y1="${y}" x2="${PAGE.drawingRight}" y2="${y}" stroke="${COLORS.frame}" stroke-width="1"/>
      <line x1="212" y1="${y}" x2="212" y2="${y + 70}" stroke="${COLORS.frame}" stroke-width="0.5"/>
      <line x1="418" y1="${y}" x2="418" y2="${y + 70}" stroke="${COLORS.frame}" stroke-width="0.5"/>
      <line x1="624" y1="${y}" x2="624" y2="${y + 70}" stroke="${COLORS.frame}" stroke-width="0.5"/>
      <line x1="624" y1="${y + 24}" x2="${PAGE.drawingRight}" y2="${y + 24}" stroke="${COLORS.frame}" stroke-width="0.5"/>
      <line x1="624" y1="${y + 48}" x2="${PAGE.drawingRight}" y2="${y + 48}" stroke="${COLORS.frame}" stroke-width="0.5"/>
      <line x1="766" y1="${y + 24}" x2="766" y2="${y + 48}" stroke="${COLORS.frame}" stroke-width="0.5"/>
      <line x1="907" y1="${y + 24}" x2="907" y2="${y + 70}" stroke="${COLORS.frame}" stroke-width="0.5"/>
      <text x="40" y="${y + 12}" font-size="6" font-weight="bold" font-family="Arial">Zeichnungsnr.</text>
      <text x="40" y="${y + 35}" font-size="16" font-family="Arial">${p.zeichnungsnummer || '1001'}</text>
      <text x="216" y="${y + 6}" font-size="5" font-weight="bold" font-family="Arial">Ausführende Firma</text>
      <text x="218" y="${y + 20}" font-size="11" font-family="Arial">${firmendaten.firmenname || ''}</text>
      <text x="218" y="${y + 35}" font-size="8" font-family="Arial" fill="#666">${firmendaten.ort || ''}</text>
      <text x="422" y="${y + 6}" font-size="5" font-weight="bold" font-family="Arial">Projekt / Adresse</text>
      <text x="424" y="${y + 20}" font-size="11" font-family="Arial">${(p.name || '').substring(0, 25)}</text>
      <text x="424" y="${y + 35}" font-size="8" font-family="Arial" fill="#666">${(p.address || '').substring(0, 30)}</text>
      <text x="628" y="${y + 18}" font-size="11" font-family="Arial">Stromlaufplan - allpolig</text>
      <text x="628" y="${y + 30}" font-size="5" font-weight="bold" font-family="Arial">Erstellt durch</text>
      <text x="628" y="${y + 43}" font-size="10" font-family="Arial">${p.ersteller || firmendaten.firmenname || ''}</text>
      <text x="911" y="${y + 30}" font-size="5" font-weight="bold" font-family="Arial">Letzte Änderung</text>
      <text x="911" y="${y + 43}" font-size="10" font-family="Arial">${new Date(p.updatedAt || Date.now()).toLocaleDateString('de-DE')}</text>
      <text x="911" y="${y + 54}" font-size="5" font-weight="bold" font-family="Arial">Blatt</text>
      <text x="911" y="${y + 67}" font-size="12" font-family="Arial">${currentPage} / ${totalPages}</text>
    `;
  };

  // Deckblatt
  const renderCoverPage = () => {
    const cx = PAGE.width / 2;
    const p = currentProject || {};
    const firma = firmendaten || {};
    
    let svg = '';
    
    // Titel oben Mitte
    svg += `<text x="${cx}" y="100" text-anchor="middle" font-size="32" font-weight="bold" font-family="Arial">Verteilerdokumentation</text>`;
    svg += `<text x="${cx}" y="135" text-anchor="middle" font-size="16" font-family="Arial">Stromlaufplan - allpolig</text>`;
    
    // Logo darunter (wenn vorhanden)
    if (firma.logo) {
      svg += `<image x="${cx - 100}" y="160" width="200" height="80" href="${firma.logo}" preserveAspectRatio="xMidYMid meet"/>`;
    }
    
    // Trennlinie
    svg += `<line x1="${PAGE.drawingLeft + 100}" y1="260" x2="${PAGE.drawingRight - 100}" y2="260" stroke="${COLORS.frame}" stroke-width="1"/>`;
    
    // Projektdaten (Mitte)
    const projektY = 300;
    svg += `<text x="${cx}" y="${projektY}" text-anchor="middle" font-size="18" font-weight="bold" font-family="Arial">Projektdaten</text>`;
    
    svg += `<text x="${cx - 200}" y="${projektY + 35}" font-size="12" font-family="Arial">Projekt:</text>`;
    svg += `<text x="${cx - 50}" y="${projektY + 35}" font-size="12" font-weight="bold" font-family="Arial">${p.name || ''}</text>`;
    
    svg += `<text x="${cx - 200}" y="${projektY + 60}" font-size="12" font-family="Arial">Adresse:</text>`;
    svg += `<text x="${cx - 50}" y="${projektY + 60}" font-size="12" font-family="Arial">${p.address || ''}</text>`;
    
    svg += `<text x="${cx - 200}" y="${projektY + 85}" font-size="12" font-family="Arial">Zeichnungsnr.:</text>`;
    svg += `<text x="${cx - 50}" y="${projektY + 85}" font-size="12" font-family="Arial">${p.zeichnungsnummer || ''}</text>`;
    
    svg += `<text x="${cx - 200}" y="${projektY + 110}" font-size="12" font-family="Arial">Ersteller:</text>`;
    svg += `<text x="${cx - 50}" y="${projektY + 110}" font-size="12" font-family="Arial">${p.ersteller || ''}</text>`;
    
    // Unterer Bereich: Ausführende Firma (links) und Verteilerdaten (rechts)
    const bottomY = 500;
    
    // Ausführende Firma - unten links
    svg += `<text x="100" y="${bottomY}" font-size="14" font-weight="bold" font-family="Arial">Ausführende Firma</text>`;
    svg += `<text x="100" y="${bottomY + 25}" font-size="12" font-weight="bold" font-family="Arial">${firma.firmenname || ''}</text>`;
    svg += `<text x="100" y="${bottomY + 45}" font-size="10" font-family="Arial">${firma.strasse || ''}</text>`;
    svg += `<text x="100" y="${bottomY + 60}" font-size="10" font-family="Arial">${firma.plz || ''} ${firma.ort || ''}</text>`;
    if (firma.telefon) {
      svg += `<text x="100" y="${bottomY + 80}" font-size="10" font-family="Arial">Tel: ${firma.telefon}</text>`;
    }
    if (firma.email) {
      svg += `<text x="100" y="${bottomY + 95}" font-size="10" font-family="Arial">${firma.email}</text>`;
    }
    
    // Verteilerdaten - unten rechts (aus den Verteiler-Einstellungen)
    svg += `<text x="600" y="${bottomY}" font-size="14" font-weight="bold" font-family="Arial">Verteilerdaten</text>`;
    
    // Hersteller
    const herstellerMap = { 'hager': 'Hager', 'abb': 'ABB', 'spelsberg': 'Spelsberg', 'siemens': 'Siemens', 'eaton': 'Eaton', 'sonstiges': p.verteilerHerstellerSonstiges || 'Sonstiges' };
    const hersteller = herstellerMap[p.verteilerHersteller] || p.verteilerHersteller || '-';
    svg += `<text x="600" y="${bottomY + 25}" font-size="10" font-family="Arial">Hersteller:</text>`;
    svg += `<text x="750" y="${bottomY + 25}" font-size="10" font-family="Arial">${hersteller}</text>`;
    
    // Verteilerbezeichnung (aus Verteiler-Einstellungen)
    svg += `<text x="600" y="${bottomY + 45}" font-size="10" font-family="Arial">Bezeichnung:</text>`;
    svg += `<text x="750" y="${bottomY + 45}" font-size="10" font-family="Arial">${p.verteilerBezeichnung || '-'}</text>`;
    
    // Verteilertyp
    const typMap = { 'unterputz': 'Unterputz (UP)', 'aufputz': 'Aufputz (AP)', 'hohlwand': 'Hohlwand (HW)', 'standverteiler': 'Standverteiler' };
    svg += `<text x="600" y="${bottomY + 65}" font-size="10" font-family="Arial">Typ:</text>`;
    svg += `<text x="750" y="${bottomY + 65}" font-size="10" font-family="Arial">${typMap[p.verteilerTyp] || p.verteilerTyp || '-'}</text>`;
    
    // TE pro Reihe
    svg += `<text x="600" y="${bottomY + 85}" font-size="10" font-family="Arial">TE pro Reihe:</text>`;
    svg += `<text x="750" y="${bottomY + 85}" font-size="10" font-family="Arial">${p.teProReihe || '12'} TE</text>`;
    
    // Anzahl Felder und Reihen
    svg += `<text x="600" y="${bottomY + 105}" font-size="10" font-family="Arial">Felder × Reihen:</text>`;
    svg += `<text x="750" y="${bottomY + 105}" font-size="10" font-family="Arial">${p.anzahlFelder || '1'} × ${p.anzahlReihen || '3'}</text>`;
    
    // Gesamtkapazität
    const gesamtTE = (parseInt(p.anzahlFelder || 1) * parseInt(p.anzahlReihen || 3) * parseInt(p.teProReihe || 12));
    svg += `<text x="600" y="${bottomY + 125}" font-size="10" font-family="Arial">Gesamtkapazität:</text>`;
    svg += `<text x="750" y="${bottomY + 125}" font-size="10" font-weight="bold" font-family="Arial">${gesamtTE} TE</text>`;
    
    return svg;
  };

  // Hauptsammelschienen
  const renderMainBusbars = (startX, endX) => {
    const busbars = [
      { label: 'L1', y: Y.busbars, color: COLORS.L1, dashed: false },
      { label: 'L2', y: Y.busbars + Y.busbarSpacing, color: COLORS.L2, dashed: false },
      { label: 'L3', y: Y.busbars + 2 * Y.busbarSpacing, color: COLORS.L3, dashed: false },
      { label: 'N', y: Y.busbars + 3 * Y.busbarSpacing, color: COLORS.N, dashed: true }
    ];
    
    return busbars.map(b => `
      <text x="${startX - 5}" y="${b.y + 4}" text-anchor="end" font-size="7" font-family="Arial" fill="${b.color}">${b.label}</text>
      <line x1="${startX}" y1="${b.y}" x2="${endX}" y2="${b.y}" stroke="${b.color}" stroke-width="1.5" ${b.dashed ? 'stroke-dasharray="6,3"' : ''}/>
      <text x="${endX + 5}" y="${b.y + 4}" font-size="7" font-family="Arial" fill="${b.color}">${b.label}</text>
    `).join('');
  };

  // PE-Schiene
  const renderPEBusbar = (startX, endX) => `
    <text x="${startX - 5}" y="${Y.peBusbar + 4}" text-anchor="end" font-size="7" font-family="Arial" fill="${COLORS.PE}">PE</text>
    <line x1="${startX}" y1="${Y.peBusbar}" x2="${endX}" y2="${Y.peBusbar}" stroke="${COLORS.PE}" stroke-width="1.5" stroke-dasharray="6,3"/>
    <text x="${endX + 5}" y="${Y.peBusbar + 4}" font-size="7" font-family="Arial" fill="${COLORS.PE}">PE</text>
  `;

  // ================================================================
  // EINSPEISUNG-SEITE - Dynamisch aus Projektdaten
  // Layout: ÜSS links (Spalte 2), SLS (Spalte 3,5,7), Zähler darunter
  // Unterstützt: Netzform (TT, TN-S, TN-C, TN-C-S, IT) und Phasenanzahl (1, 2, 3)
  // ================================================================
  const renderEinspeisungPage = (pageData) => {
    let svg = '';
    const startX = 80;
    const spacing = 15;
    const einsp = pageData.einspeisung;
    const netzform = einsp?.data?.netzform || 'TN-C-S';
    const phasenanzahl = parseInt(einsp?.data?.phasenanzahl) || 3;
    
    // Netzform-Eigenschaften bestimmen
    const isTNC = netzform === 'TN-C';        // PEN durchgehend, keine Auftrennung
    const isTNCS = netzform === 'TN-C-S';     // PEN in Zuleitung, wird aufgetrennt
    const isTNS = netzform === 'TN-S';        // N und PE durchgehend getrennt
    const isTT = netzform === 'TT';           // PE über lokalen Erder (HES)
    const isIT = netzform === 'IT';           // Kein N, PE nur lokal
    
    // Farben für Leiter
    const PEN_COLOR = '#0d4d1a'; // Grüngelb für PEN
    
    // Phasenlabels basierend auf Phasenanzahl
    const phaseLabels = phasenanzahl === 1 ? ['L1'] : 
                        phasenanzahl === 2 ? ['L1', 'L2'] : ['L1', 'L2', 'L3'];
    
    // Hilfsfunktion: Dreieck-Symbol für Zuleitung zeichnen
    const drawTriangle = (x, color) => 
      `<path fill="none" stroke="${color}" stroke-width="0.6" d="M${x}.001.712 ${x - 4.861} 10.7h10.076Z"/>`;
    
    // ================================================================
    // ZULEITUNG-SYMBOL (unten links) - abhängig von Netzform & Phasenanzahl
    // ================================================================
    const zuleitungY = 572;
    const triangleSpacing = 15;
    let zuleitungWidth = 0;
    
    svg += `<g transform="translate(${startX - 20}, ${zuleitungY})">`;
    
    // Phase(n) zeichnen
    phaseLabels.forEach((label, i) => {
      const x = 15 + i * triangleSpacing;
      svg += drawTriangle(x, '#000');
      svg += `<text x="${x}" y="18" font-size="7" font-family="sans-serif" text-anchor="middle">${label}</text>`;
      zuleitungWidth = x + 10;
    });
    
    // N/PEN-Leiter zeichnen (außer bei IT)
    if (!isIT) {
      const nX = 15 + phasenanzahl * triangleSpacing;
      
      if (isTNC) {
        // TN-C: PEN-Leiter (grün-gelb)
        svg += drawTriangle(nX, PEN_COLOR);
        svg += `<text x="${nX}" y="18" font-size="7" font-family="sans-serif" text-anchor="middle" fill="${PEN_COLOR}">PEN</text>`;
        zuleitungWidth = nX + 15;
      } else if (isTNCS) {
        // TN-C-S: PEN kommt an, wird aufgetrennt -> Symbol zeigt PEN
        svg += drawTriangle(nX, PEN_COLOR);
        svg += `<text x="${nX}" y="18" font-size="7" font-family="sans-serif" text-anchor="middle" fill="${PEN_COLOR}">PEN</text>`;
        zuleitungWidth = nX + 15;
      } else {
        // TN-S, TT: Separater N-Leiter
        svg += drawTriangle(nX, COLORS.N);
        svg += `<text x="${nX}" y="18" font-size="7" font-family="sans-serif" text-anchor="middle" fill="${COLORS.N}">N</text>`;
        zuleitungWidth = nX + 10;
        
        // TN-S: Zusätzlicher PE-Leiter in Zuleitung
        if (isTNS) {
          const peX = nX + triangleSpacing;
          svg += drawTriangle(peX, COLORS.PE);
          svg += `<text x="${peX}" y="18" font-size="7" font-family="sans-serif" text-anchor="middle" fill="${COLORS.PE}">PE</text>`;
          zuleitungWidth = peX + 10;
        }
      }
    }
    
    svg += `</g>`;
    
    // ================================================================
    // LOKALER ERDER für TT und IT Netze
    // Nur Erdungssymbol UNTERHALB der PE-Linie, bildet Anfang der PE-Schiene
    // Position: Rechts von der Einspeisung (ca. x=150)
    // ================================================================
    if (isTT || isIT) {
      const erderX = 150; // Rechts von der Einspeisung
      
      // Erdungssymbol UNTERHALB der PE-Schiene
      svg += `<line x1="${erderX}" y1="${Y.peBusbar}" x2="${erderX}" y2="${Y.peBusbar + 25}" stroke="${COLORS.PE}" stroke-width="0.8"/>`;
      svg += `<line x1="${erderX - 10}" y1="${Y.peBusbar + 25}" x2="${erderX + 10}" y2="${Y.peBusbar + 25}" stroke="${COLORS.PE}" stroke-width="1.2"/>`;
      svg += `<line x1="${erderX - 7}" y1="${Y.peBusbar + 30}" x2="${erderX + 7}" y2="${Y.peBusbar + 30}" stroke="${COLORS.PE}" stroke-width="0.8"/>`;
      svg += `<line x1="${erderX - 4}" y1="${Y.peBusbar + 35}" x2="${erderX + 4}" y2="${Y.peBusbar + 35}" stroke="${COLORS.PE}" stroke-width="0.5"/>`;
      
      // Verbindungspunkt auf PE-Schiene
      svg += `<circle cx="${erderX}" cy="${Y.peBusbar}" r="2" fill="${COLORS.PE}"/>`;
    }

    // ================================================================
    // BESCHRIFTUNG DER ZULEITUNG
    // ================================================================
    if (einsp) {
      const kabeltyp = einsp.data?.kabeltyp || 'NYM-J';
      const querschnitt = einsp.data?.querschnitt || '16';
      // Aderanzahl in der ZULEITUNG berechnen (nicht nach Auftrennung!)
      // TN-C-S: L1,L2,L3,PEN = 4 Adern (PEN wird erst im Verteiler aufgetrennt)
      // TN-S: L1,L2,L3,N,PE = 5 Adern
      // TN-C: L1,L2,L3,PEN = 4 Adern (PEN bleibt kombiniert)
      // TT: L1,L2,L3,N = 4 Adern (PE lokal von HES)
      // IT: L1,L2,L3 = 3 Adern (kein N, PE lokal)
      let aderanzahl = phasenanzahl;
      if (isTNS) {
        aderanzahl += 2; // N + PE beide in Zuleitung
      } else if (!isIT) {
        aderanzahl += 1; // N oder PEN (TN-C, TN-C-S, TT)
      }
      // IT hat keine zusätzlichen Leiter
      
      const einspeisungTyp = einsp.data?.einspeisungTyp || 'hauptleitungsklemme';
      const einspeisungTexte = {
        'sammelschiene_adapter': 'Sammelschiene m. Adapter',
        'sammelschiene_klemmen': 'Sammelschiene m. Klemmen',
        'hauptleitungsklemme': 'Hauptleitungsklemme'
      };
      const einspeisungText = einspeisungTexte[einspeisungTyp] || 'Hauptleitungsklemme';
      
      svg += `<text x="${startX + 30}" y="605" font-size="8" font-family="sans-serif" text-anchor="middle">${einsp.bezeichnung || 'Zuleitung'}</text>`;
      svg += `<text x="${startX + 30}" y="615" font-size="7" font-family="sans-serif" text-anchor="middle">${kabeltyp} ${aderanzahl}x${querschnitt}mm²</text>`;
      svg += `<text x="${startX + 30}" y="625" font-size="6" font-family="sans-serif" text-anchor="middle" fill="#666">${netzform}</text>`;
      svg += `<text x="${startX + 30}" y="635" font-size="5" font-family="sans-serif" text-anchor="middle" fill="#999">${einspeisungText}</text>`;
    }

    // ================================================================
    // VERTIKALE LEITUNGEN VON ZULEITUNG ZU SAMMELSCHIENEN
    // ================================================================
    // Phasenleitungen (L1, L2, L3)
    for (let i = 0; i < phasenanzahl; i++) {
      const x = startX + i * spacing;
      const busY = 90 + i * 15;
      svg += `<line x1="${x}" y1="${zuleitungY}" x2="${x}" y2="${busY}" stroke="#000" stroke-width="0.6"/>`;
    }
    
    // N/PEN-Leitung
    const nLineX = startX + phasenanzahl * spacing;
    const nBusY = 90 + 3 * 15; // N immer auf Position 4 (y=135)
    
    if (isTNC) {
      // TN-C: PEN durchgehend zu N-Schiene (keine Auftrennung)
      svg += `<line x1="${nLineX}" y1="${zuleitungY}" x2="${nLineX}" y2="${nBusY}" stroke="${PEN_COLOR}" stroke-width="0.8"/>`;
      // PEN-Brücke zur PE-Schiene
      svg += `<line x1="${nLineX}" y1="${nBusY}" x2="${nLineX}" y2="${Y.peBusbar}" stroke="${PEN_COLOR}" stroke-width="0.6" stroke-dasharray="3,2"/>`;
      svg += `<line x1="${nLineX}" y1="${Y.peBusbar}" x2="${nLineX + 40}" y2="${Y.peBusbar}" stroke="${PEN_COLOR}" stroke-width="0.6" stroke-dasharray="3,2"/>`;
    } else if (isTNCS) {
      // TN-C-S: PEN kommt an, wird aufgetrennt in N und PE
      // Kleiner schwarzer Kasten für die Auftrennung
      const splitY = Y.peBusbar - 30; // Näher an PE-Schiene
      
      // PEN-Leitung von Zuleitung bis zur N-Schiene
      svg += `<line x1="${nLineX}" y1="${zuleitungY}" x2="${nLineX}" y2="${nBusY}" stroke="#000" stroke-width="0.8"/>`;
      svg += `<circle cx="${nLineX}" cy="${nBusY}" r="2" fill="#000"/>`;
      
      // PEN weiter von N-Schiene zum Auftrennungspunkt
      svg += `<line x1="${nLineX}" y1="${nBusY}" x2="${nLineX}" y2="${splitY}" stroke="#000" stroke-width="0.6"/>`;
      
      // PEN-Auftrennungs-Symbol - kleiner schwarzer Kasten
      const boxW = 16;
      const boxH = 10;
      const boxX = nLineX - boxW/2;
      const boxY = splitY;
      
      svg += `<rect x="${boxX}" y="${boxY}" width="${boxW}" height="${boxH}" fill="#fff" stroke="#000" stroke-width="0.8"/>`;
      
      // PE zur PE-Schiene (rechts vom Kasten)
      svg += `<line x1="${nLineX + boxW/2}" y1="${boxY + boxH/2}" x2="${nLineX + boxW/2 + 10}" y2="${boxY + boxH/2}" stroke="#000" stroke-width="0.5"/>`;
      svg += `<line x1="${nLineX + boxW/2 + 10}" y1="${boxY + boxH/2}" x2="${nLineX + boxW/2 + 10}" y2="${Y.peBusbar}" stroke="${COLORS.PE}" stroke-width="0.6" stroke-dasharray="3,2"/>`;
      svg += `<circle cx="${nLineX + boxW/2 + 10}" cy="${Y.peBusbar}" r="2" fill="${COLORS.PE}"/>`;
    } else if (isTNS) {
      // TN-S: N und PE beide aus Zuleitung, getrennt
      // N-Leitung
      svg += `<line x1="${nLineX}" y1="${zuleitungY}" x2="${nLineX}" y2="${nBusY}" stroke="${COLORS.N}" stroke-width="0.6" stroke-dasharray="4,4"/>`;
      // PE-Leitung (separat) - direkt zur PE-Schiene
      const peLineX = nLineX + spacing;
      svg += `<line x1="${peLineX}" y1="${zuleitungY}" x2="${peLineX}" y2="${Y.peBusbar}" stroke="${COLORS.PE}" stroke-width="0.8" stroke-dasharray="3,2"/>`;
      svg += `<circle cx="${peLineX}" cy="${Y.peBusbar}" r="2.5" fill="${COLORS.PE}"/>`;
    } else if (isTT) {
      // TT: N aus Zuleitung, PE kommt von lokaler HES (wird oben gezeichnet)
      svg += `<line x1="${nLineX}" y1="${zuleitungY}" x2="${nLineX}" y2="${nBusY}" stroke="${COLORS.N}" stroke-width="0.6" stroke-dasharray="4,4"/>`;
      // PE-Verbindung von HES erfolgt im HES-Block oben
    } else if (isIT) {
      // IT: Kein N-Leiter! PE kommt von lokaler HES (wird oben gezeichnet)
      // PE-Verbindung von HES erfolgt im HES-Block oben
    }

    // ================================================================
    // HAUPTSAMMELSCHIENEN (L1, L2, L3, N)
    // ================================================================
    const slsCount = pageData.slsList?.length || 0;
    const busbarEndX = slsCount > 0 ? 200 + slsCount * 120 : 350;
    
    // Phasenschienen
    for (let i = 0; i < phasenanzahl; i++) {
      const y = 90 + i * 15;
      const lineStartX = startX + i * spacing;
      svg += `<line x1="${lineStartX}" y1="${y}" x2="${busbarEndX}" y2="${y}" stroke="#000" stroke-width="0.8"/>`;
    }
    
    // N-Schiene (außer bei IT)
    if (!isIT) {
      const nSchienStartX = isTNCS ? (nLineX - 8) : nLineX;
      svg += `<line x1="${nSchienStartX}" y1="${nBusY}" x2="${busbarEndX}" y2="${nBusY}" stroke="${COLORS.N}" stroke-width="0.6" stroke-dasharray="4,4"/>`;
    }
    
    // PE-Schiene (immer vorhanden)
    // Bei TT/IT startet sie vom Erder-Punkt (x=150)
    // Bei TN-C-S vom Auftrennungspunkt
    const peSchienStartX = isTNC ? (nLineX + 40) : 
                           isTNCS ? (nLineX + 18) : 
                           isTNS ? (nLineX + spacing) : 
                           150; // TT/IT: Vom Erder
    svg += `<line x1="${peSchienStartX}" y1="${Y.peBusbar}" x2="${busbarEndX + 100}" y2="${Y.peBusbar}" stroke="${COLORS.PE}" stroke-width="0.6" stroke-dasharray="3,2"/>`;

    // ================================================================
    // BESCHRIFTUNGEN LINKS (Schienenbezeichnungen)
    // ================================================================
    phaseLabels.forEach((label, i) => {
      svg += `<text x="55" y="${93 + i * 15}" text-anchor="end" font-size="6" font-family="sans-serif">${label}</text>`;
    });
    
    if (!isIT) {
      const nLabel = isTNC ? 'PEN' : 'N';
      const nColor = isTNC ? PEN_COLOR : COLORS.N;
      svg += `<text x="55" y="138" text-anchor="end" font-size="6" font-family="sans-serif" fill="${nColor}">${nLabel}</text>`;
    }
    svg += `<text x="55" y="${Y.peBusbar + 3}" text-anchor="end" font-size="6" font-family="sans-serif" fill="${COLORS.PE}">PE</text>`;

    // Komponenten-Layout - mit genug Abstand um Überlagerungen zu vermeiden
    // ÜSS weiter rechts (x=180) um Platz für Beschriftung zu haben
    // SLS ab x=280 mit 120px Abstand
    
    // ÜSS - weiter rechts für Beschriftungsplatz
    if (pageData.ueberspannungsschutz) {
      svg += renderUSSSymbolNew(180, pageData.ueberspannungsschutz, 2);
    }

    // SLS - mit genug Abstand
    const slsPositions = [280, 400, 520];
    const slsBmkIndices = [1, 3, 4];
    (pageData.slsList || []).forEach((sls, idx) => {
      if (idx < 3) {
        svg += renderSLSSymbol(slsPositions[idx], sls, slsBmkIndices[idx]);
      }
    });

    // Zähler - unter SLS 1 (wenn SLS vorhanden)
    if (pageData.zaehler && slsCount > 0) {
      svg += renderZaehlerSymbol(slsPositions[0], pageData.zaehler);
    }

    // Seitenverweis zu nächster Seite
    if (totalPages > 2) {
      svg += `<circle cx="${busbarEndX + 50}" cy="580" r="8" fill="none" stroke="#000" stroke-width="0.6"/>`;
      svg += `<text x="${busbarEndX + 50}" y="583" font-size="8" font-family="sans-serif" text-anchor="middle">→</text>`;
      svg += `<text x="${busbarEndX + 50}" y="595" font-size="6" font-family="sans-serif" text-anchor="middle">/3.1</text>`;
    }

    return svg;
  };

  // Neues ÜSS Symbol - auf Höhe des Zählers, greift von Sammelschienen ab
  // BMK: Geräte VOR dem Zähler haben F0.x (z.B. -F0.2 für ÜSS nach SLS)
  const renderUSSSymbolNew = (x, ussData, ussIndex = 2) => {
    const y = 280;  // Auf Zähler-Höhe
    const d = ussData.data || {};
    // BMK: Wenn vorhanden aus data.bmk, sonst F0.x für Geräte vor dem Zähler
    const bmk = d.bmk || `-F0.${ussIndex}`;
    const beschreibung = ussData.bezeichnung || 'Überspannungsschutz';
    const ussTyp = d.ussTyp || 'Typ1+2';
    const ussPhasen = d.ussPhasen || '3P+N';
    const is3Phase = ussPhasen === '3P+N' || ussPhasen === '3P';

    let svg = '';
    
    // Beschriftung links - BMK fett, Typ und Beschreibung darunter
    svg += `<text x="${x - 5}" y="${y - 5}" text-anchor="end" font-size="8" font-weight="bold" font-family="Arial">${bmk}</text>`;
    svg += `<text x="${x - 5}" y="${y + 5}" text-anchor="end" font-size="6" font-family="Arial">${beschreibung}</text>`;
    svg += `<text x="${x - 5}" y="${y + 13}" text-anchor="end" font-size="5" font-family="Arial">${ussTyp}</text>`;

    if (is3Phase) {
      // 3-phasig + N: 4 Varistoren
      const poleSpacing = 12;
      
      // Rechteck um alle Varistoren
      svg += `<rect x="${x}" y="${y}" width="55" height="50" fill="none" stroke="#000" stroke-width="0.6"/>`;
      
      for (let i = 0; i < 4; i++) {
        const px = x + 5 + i * poleSpacing;
        const busY = i < 3 ? (90 + i * 15) : 135; // L1, L2, L3, N
        const color = i < 3 ? '#000' : COLORS.N;
        const dashed = i === 3;
        
        // Vertikale Leitung von Sammelschiene zum ÜSS
        svg += `<line x1="${px}" y1="${busY}" x2="${px}" y2="${y}" stroke="${color}" stroke-width="0.5" ${dashed ? 'stroke-dasharray="3,2"' : ''}/>`;
        svg += `<circle cx="${px}" cy="${busY}" r="2" fill="${color}"/>`;
        
        // Varistor-Symbol (Rechteck mit Blitz)
        svg += `<rect x="${px - 4}" y="${y + 5}" width="8" height="25" fill="none" stroke="${color}" stroke-width="0.4"/>`;
        // Blitz im Varistor
        svg += `<path d="M${px} ${y + 10} L${px - 3} ${y + 18} L${px + 3} ${y + 18} L${px} ${y + 26}" fill="none" stroke="${color}" stroke-width="0.6"/>`;
        svg += `<polygon points="${px},${y + 26} ${px + 2},${y + 22} ${px - 2},${y + 24}" fill="${color}"/>`;
      }
      
      // PE-Anschluss von ÜSS zur PE-Schiene
      svg += `<line x1="${x + 20}" y1="${y + 50}" x2="${x + 20}" y2="${Y.peBusbar}" stroke="${COLORS.PE}" stroke-width="0.8" stroke-dasharray="3,2"/>`;
      svg += `<circle cx="${x + 20}" cy="${Y.peBusbar}" r="2.5" fill="${COLORS.PE}"/>`;
      svg += `<text x="${x + 25}" y="${y + 70}" font-size="5" font-family="Arial" fill="${COLORS.PE}">PE</text>`;
      
      // PAS (Potenzialausgleichsschiene) - zusätzlicher Erdsymbol
      svg += `<line x1="${x + 40}" y1="${y + 50}" x2="${x + 40}" y2="${y + 85}" stroke="${COLORS.PE}" stroke-width="0.5"/>`;
      // Erdsymbol
      svg += `<line x1="${x + 35}" y1="${y + 85}" x2="${x + 45}" y2="${y + 85}" stroke="${COLORS.PE}" stroke-width="0.8"/>`;
      svg += `<line x1="${x + 37}" y1="${y + 88}" x2="${x + 43}" y2="${y + 88}" stroke="${COLORS.PE}" stroke-width="0.6"/>`;
      svg += `<line x1="${x + 39}" y1="${y + 91}" x2="${x + 41}" y2="${y + 91}" stroke="${COLORS.PE}" stroke-width="0.4"/>`;
      svg += `<text x="${x + 50}" y="${y + 88}" font-size="5" font-family="Arial" fill="${COLORS.PE}">PAS</text>`;
      
    } else {
      // 1-phasig: 1 Varistor
      svg += `<rect x="${x}" y="${y}" width="30" height="50" fill="none" stroke="#000" stroke-width="0.6"/>`;
      
      // Abgriff von L1
      svg += `<line x1="${x + 15}" y1="90" x2="${x + 15}" y2="${y}" stroke="#000" stroke-width="0.5"/>`;
      svg += `<circle cx="${x + 15}" cy="90" r="2" fill="#000"/>`;
      
      // Varistor mit Blitz
      svg += `<rect x="${x + 7}" y="${y + 5}" width="16" height="30" fill="none" stroke="#000" stroke-width="0.4"/>`;
      svg += `<path d="M${x + 15} ${y + 10} L${x + 10} ${y + 20} L${x + 20} ${y + 20} L${x + 15} ${y + 30}" fill="none" stroke="#000" stroke-width="0.6"/>`;
      svg += `<polygon points="${x + 15},${y + 30} ${x + 17},${y + 25} ${x + 13},${y + 27}" fill="#000"/>`;
      
      // PE zur PE-Schiene
      svg += `<line x1="${x + 15}" y1="${y + 50}" x2="${x + 15}" y2="${Y.peBusbar}" stroke="${COLORS.PE}" stroke-width="0.8" stroke-dasharray="3,2"/>`;
      svg += `<circle cx="${x + 15}" cy="${Y.peBusbar}" r="2.5" fill="${COLORS.PE}"/>`;
    }

    return svg;
  };

  // SLS Symbol - mit Ausgängen zum Zähler
  // BMK: Geräte VOR dem Zähler haben F0.x (z.B. -F0.1 für SLS)
  const renderSLSSymbol = (x, slsData, slsIndex = 1) => {
    const y = 160;
    const d = slsData.data || {};
    // BMK: Wenn vorhanden aus data.bmk, sonst F0.x für Geräte vor dem Zähler
    const bmk = d.bmk || `-F0.${slsIndex}`;
    const beschreibung = slsData.bezeichnung || 'SLS';
    const nennstrom = d.nennstrom || '35';
    const pole = d.pole || '3';
    const poleSpacing = 12;

    let svg = '';
    
    // Beschriftung - BMK fett, Bezeichnung, Nennstrom und Typ darunter
    svg += `<text x="${x - 5}" y="${y + 10}" text-anchor="end" font-size="9" font-weight="bold" font-family="Arial">${bmk}</text>`;
    svg += `<text x="${x - 5}" y="${y + 20}" text-anchor="end" font-size="6" font-family="Arial">${beschreibung}</text>`;
    svg += `<text x="${x - 5}" y="${y + 30}" text-anchor="end" font-size="7" font-family="Arial">${nennstrom}A</text>`;
    svg += `<text x="${x - 5}" y="${y + 40}" text-anchor="end" font-size="5" font-family="Arial" fill="#666">SLS</text>`;

    // 3 Pole (L1, L2, L3)
    for (let i = 0; i < 3; i++) {
      const px = x + i * poleSpacing;
      const busY = 90 + i * 15;
      
      // Zuleitung von Sammelschiene
      svg += `<line x1="${px}" y1="${busY}" x2="${px}" y2="${y}" stroke="#000" stroke-width="0.6"/>`;
      svg += `<circle cx="${px}" cy="${busY}" r="2" fill="#000"/>`;
      
      // Kontakte
      svg += `<circle cx="${px}" cy="${y}" r="1.5" fill="none" stroke="#000" stroke-width="0.5"/>`;
      svg += `<line x1="${px}" y1="${y + 2}" x2="${px + 4}" y2="${y + 25}" stroke="#000" stroke-width="0.6"/>`;
      svg += `<circle cx="${px}" cy="${y + 30}" r="1.5" fill="#000"/>`;
      
      // Ausgang zum Zähler (bis y=270, Zähler-Eingang)
      svg += `<line x1="${px}" y1="${y + 32}" x2="${px}" y2="270" stroke="#000" stroke-width="0.5"/>`;
    }
    
    // "S" für selektiv (nur einmal)
    svg += `<text x="${x + poleSpacing + 6}" y="${y + 15}" font-size="5" font-family="Arial" font-weight="bold">S</text>`;

    // Mechanische Kopplung
    svg += `<line x1="${x}" y1="${y + 15}" x2="${x + 2 * poleSpacing}" y2="${y + 15}" stroke="#000" stroke-width="0.5" stroke-dasharray="2,1"/>`;

    // N-Durchführung (parallel zum SLS, führt auch zum Zähler)
    const nX = x + 3 * poleSpacing + 5;
    svg += `<line x1="${nX}" y1="135" x2="${nX}" y2="270" stroke="${COLORS.N}" stroke-width="0.5" stroke-dasharray="4,2"/>`;
    svg += `<circle cx="${nX}" cy="135" r="2" fill="${COLORS.N}"/>`;

    return svg;
  };

  // Zähler Symbol - mit Anschlüssen oben (vom SLS) und unten (zur Klemme)
  // Erweitert um: Messart, S0-Schnittstelle, Tarifumschaltung, Verdrahtung
  const renderZaehlerSymbol = (x, zaehlerData) => {
    const y = 280;
    const d = zaehlerData.data || {};
    // BMK: Wenn vorhanden aus data.bmk, sonst aus bezeichnung extrahieren oder Fallback
    const bmk = d.bmk || (zaehlerData.bezeichnung?.startsWith('-') ? zaehlerData.bezeichnung?.split(' ')[0] : '-P1');
    const beschreibung = zaehlerData.bezeichnung || 'EVU Zähler';
    
    // Erweiterte Eigenschaften
    const messart = d.messart || 'direkt'; // 'direkt' oder 'wandler'
    const zaehlerart = d.zaehlerart || 'drehstrom'; // 'drehstrom', 'wechselstrom', 'zweirichtung'
    const verdrahtung = d.verdrahtung || '16'; // Aderquerschnitt in mm²
    const s0Schnittstelle = d.s0Schnittstelle || false;
    const tarifumschaltung = d.tarifumschaltung || false;
    const montageart = d.montageart || 'ehz'; // 'ehz', 'bke', 'dreipunkt'
    
    const isWandler = messart === 'wandler';
    const isDrehstrom = zaehlerart === 'drehstrom' || zaehlerart === 'zweirichtung';
    const isZweirichtung = zaehlerart === 'zweirichtung';

    let svg = '';
    
    // Beschriftung links - BMK fett, Beschreibung darunter
    svg += `<text x="${x - 5}" y="${y}" text-anchor="end" font-size="8" font-weight="bold" font-family="Arial">${bmk}</text>`;
    svg += `<text x="${x - 5}" y="${y + 10}" text-anchor="end" font-size="6" font-family="Arial">${beschreibung}</text>`;
    
    // Zusatz-Info: Zählerart
    if (isDrehstrom) {
      svg += `<text x="${x - 5}" y="${y + 18}" text-anchor="end" font-size="5" font-family="Arial" fill="#666">Drehstrom</text>`;
    } else {
      svg += `<text x="${x - 5}" y="${y + 18}" text-anchor="end" font-size="5" font-family="Arial" fill="#666">Wechselstrom</text>`;
    }
    
    // Zweirichtung-Symbol (Pfeile in beide Richtungen)
    if (isZweirichtung) {
      svg += `<text x="${x - 5}" y="${y + 26}" text-anchor="end" font-size="4" font-family="Arial" fill="#666">↔ Zweirichtung</text>`;
    }
    
    // Zähler-Rechteck (größer wenn Wandlermessung)
    const rectWidth = isWandler ? 70 : 50;
    const rectHeight = 50;
    svg += `<rect x="${x}" y="${y}" width="${rectWidth}" height="${rectHeight}" fill="none" stroke="#000" stroke-width="0.6"/>`;
    
    // Kreis mit kWh
    const circleX = x + (isWandler ? 35 : 25);
    svg += `<circle cx="${circleX}" cy="${y + 25}" r="15" fill="none" stroke="#000" stroke-width="0.4"/>`;
    svg += `<text x="${circleX}" y="${y + 28}" text-anchor="middle" font-size="8" font-family="Arial">kWh</text>`;
    
    // Wandlermessung: zusätzliche Stromwandler-Symbole
    if (isWandler) {
      // Stromwandler-Symbol (Rechteck mit Wicklungen)
      svg += `<rect x="${x + 55}" y="${y + 8}" width="10" height="35" fill="none" stroke="#000" stroke-width="0.4"/>`;
      svg += `<text x="${x + 60}" y="${y + 28}" text-anchor="middle" font-size="5" font-family="Arial" transform="rotate(-90, ${x + 60}, ${y + 28})">Wandler</text>`;
      
      // "W" für Wandlermessung
      svg += `<text x="${x + 5}" y="${y + 45}" font-size="6" font-family="Arial" font-weight="bold">W</text>`;
    }
    
    // Anschlüsse
    const topY = y;
    const bottomY = y + rectHeight;
    const spacing = isWandler ? 14 : 10;
    const numConnections = isDrehstrom ? 4 : 2; // 4 für Drehstrom (L1,L2,L3,N), 2 für Wechselstrom (L,N)
    
    for (let i = 0; i < numConnections; i++) {
      const px = x + 5 + i * spacing;
      const isN = (isDrehstrom && i === 3) || (!isDrehstrom && i === 1);
      const color = isN ? COLORS.N : '#000';
      const dashed = isN;
      
      // Eingang oben
      svg += `<line x1="${px}" y1="${topY - 10}" x2="${px}" y2="${topY}" stroke="${color}" stroke-width="0.5" ${dashed ? 'stroke-dasharray="3,2"' : ''}/>`;
      svg += `<circle cx="${px}" cy="${topY}" r="1.5" fill="${color}"/>`;
      
      // Ausgang unten
      svg += `<circle cx="${px}" cy="${bottomY}" r="1.5" fill="${color}"/>`;
      svg += `<line x1="${px}" y1="${bottomY}" x2="${px}" y2="${bottomY + 15}" stroke="${color}" stroke-width="0.5" ${dashed ? 'stroke-dasharray="3,2"' : ''}/>`;
    }
    
    // Beschriftung der Anschlüsse (nach DIN VDE Standard)
    if (isDrehstrom) {
      // Eingänge: 1, 2, 3, N (oder 7 für N)
      svg += `<text x="${x + 5}" y="${topY - 15}" font-size="5" font-family="Arial" text-anchor="middle">1</text>`;
      svg += `<text x="${x + 5 + spacing}" y="${topY - 15}" font-size="5" font-family="Arial" text-anchor="middle">2</text>`;
      svg += `<text x="${x + 5 + 2*spacing}" y="${topY - 15}" font-size="5" font-family="Arial" text-anchor="middle">3</text>`;
      svg += `<text x="${x + 5 + 3*spacing}" y="${topY - 15}" font-size="5" font-family="Arial" text-anchor="middle" fill="${COLORS.N}">N</text>`;
      
      // Ausgänge: 4, 5, 6, N (oder 8 für N)
      svg += `<text x="${x + 5}" y="${bottomY + 25}" font-size="5" font-family="Arial" text-anchor="middle">4</text>`;
      svg += `<text x="${x + 5 + spacing}" y="${bottomY + 25}" font-size="5" font-family="Arial" text-anchor="middle">5</text>`;
      svg += `<text x="${x + 5 + 2*spacing}" y="${bottomY + 25}" font-size="5" font-family="Arial" text-anchor="middle">6</text>`;
      svg += `<text x="${x + 5 + 3*spacing}" y="${bottomY + 25}" font-size="5" font-family="Arial" text-anchor="middle" fill="${COLORS.N}">N</text>`;
    } else {
      // Wechselstrom: L, N
      svg += `<text x="${x + 5}" y="${topY - 15}" font-size="5" font-family="Arial" text-anchor="middle">L</text>`;
      svg += `<text x="${x + 5 + spacing}" y="${topY - 15}" font-size="5" font-family="Arial" text-anchor="middle" fill="${COLORS.N}">N</text>`;
      
      svg += `<text x="${x + 5}" y="${bottomY + 25}" font-size="5" font-family="Arial" text-anchor="middle">L'</text>`;
      svg += `<text x="${x + 5 + spacing}" y="${bottomY + 25}" font-size="5" font-family="Arial" text-anchor="middle" fill="${COLORS.N}">N'</text>`;
    }
    
    // S0-Schnittstelle (Impulsausgang) - rechts unten am Zähler
    if (s0Schnittstelle) {
      const s0X = x + rectWidth + 5;
      const s0Y = y + 35;
      
      // Anschluss-Punkte
      svg += `<circle cx="${s0X}" cy="${s0Y}" r="1.5" fill="#000"/>`;
      svg += `<circle cx="${s0X}" cy="${s0Y + 8}" r="1.5" fill="#000"/>`;
      
      // Linie vom Zähler
      svg += `<line x1="${x + rectWidth}" y1="${s0Y}" x2="${s0X}" y2="${s0Y}" stroke="#000" stroke-width="0.4"/>`;
      svg += `<line x1="${x + rectWidth}" y1="${s0Y + 8}" x2="${s0X}" y2="${s0Y + 8}" stroke="#000" stroke-width="0.4"/>`;
      
      // Beschriftung
      svg += `<text x="${s0X + 3}" y="${s0Y + 5}" font-size="4" font-family="Arial">S0</text>`;
      svg += `<text x="${s0X + 3}" y="${s0Y + 12}" font-size="3" font-family="Arial" fill="#666">(Impuls)</text>`;
    }
    
    // Tarifumschaltung (Steuereingang) - rechts oben am Zähler
    if (tarifumschaltung) {
      const tarX = x + rectWidth + 5;
      const tarY = y + 10;
      
      // Anschluss-Punkte
      svg += `<circle cx="${tarX}" cy="${tarY}" r="1.5" fill="#000"/>`;
      svg += `<circle cx="${tarX}" cy="${tarY + 8}" r="1.5" fill="#000"/>`;
      
      // Linie vom Zähler
      svg += `<line x1="${x + rectWidth}" y1="${tarY}" x2="${tarX}" y2="${tarY}" stroke="#000" stroke-width="0.4"/>`;
      svg += `<line x1="${x + rectWidth}" y1="${tarY + 8}" x2="${tarX}" y2="${tarY + 8}" stroke="#000" stroke-width="0.4"/>`;
      
      // Beschriftung
      svg += `<text x="${tarX + 3}" y="${tarY + 5}" font-size="4" font-family="Arial">HT/NT</text>`;
      svg += `<text x="${tarX + 3}" y="${tarY + 12}" font-size="3" font-family="Arial" fill="#666">(Tarif)</text>`;
    }
    
    // Verdrahtungsquerschnitt - unter den Ausgängen
    svg += `<text x="${x + (rectWidth/2)}" y="${bottomY + 35}" text-anchor="middle" font-size="5" font-family="Arial" fill="#333">${verdrahtung}mm²</text>`;
    
    // Montageart-Symbol
    if (montageart === 'ehz') {
      svg += `<text x="${x + rectWidth - 5}" y="${y + 8}" font-size="4" font-family="Arial" fill="#666">eHZ</text>`;
    } else if (montageart === 'bke') {
      svg += `<text x="${x + rectWidth - 5}" y="${y + 8}" font-size="4" font-family="Arial" fill="#666">BKE</text>`;
    }

    return svg;
  };

  // ÜSS Symbol (alte Version für Kompatibilität)
  // BMK: Geräte VOR dem Zähler haben F0.x
  const renderUSSSymbol = (x, ussData, ussIndex = 2) => {
    const y = 160;
    const d = ussData.data || {};
    // BMK: Wenn vorhanden aus data.bmk, sonst F0.x für Geräte vor dem Zähler
    const bmk = d.bmk || `-F0.${ussIndex}`;
    const beschreibung = ussData.bezeichnung || 'ÜSS';

    let svg = '';
    
    svg += `<text x="${x - 5}" y="${y + 10}" text-anchor="end" font-size="8" font-weight="bold" font-family="Arial">${bmk}</text>`;
    svg += `<text x="${x - 5}" y="${y + 20}" text-anchor="end" font-size="6" font-family="Arial">${beschreibung}</text>`;
    svg += `<text x="${x - 5}" y="${y + 28}" text-anchor="end" font-size="5" font-family="Arial">Typ 1+2</text>`;

    // Rechteck
    svg += `<rect x="${x}" y="${y}" width="30" height="35" fill="none" stroke="#000" stroke-width="0.6"/>`;
    
    // Blitz-Symbol
    svg += `<path d="M${x + 15} ${y + 5} L${x + 10} ${y + 17} L${x + 15} ${y + 17} L${x + 10} ${y + 30}" fill="none" stroke="#000" stroke-width="0.8"/>`;
    svg += `<polygon points="${x + 10},${y + 30} ${x + 12},${y + 25} ${x + 8},${y + 27}" fill="#000"/>`;

    // Erdungsanschluss
    svg += `<line x1="${x + 15}" y1="${y + 35}" x2="${x + 15}" y2="${y + 50}" stroke="${COLORS.PE}" stroke-width="0.6" stroke-dasharray="3,2"/>`;
    svg += `<line x1="${x + 10}" y1="${y + 50}" x2="${x + 20}" y2="${y + 50}" stroke="${COLORS.PE}" stroke-width="0.8"/>`;
    svg += `<line x1="${x + 12}" y1="${y + 53}" x2="${x + 18}" y2="${y + 53}" stroke="${COLORS.PE}" stroke-width="0.6"/>`;
    svg += `<line x1="${x + 14}" y1="${y + 56}" x2="${x + 16}" y2="${y + 56}" stroke="${COLORS.PE}" stroke-width="0.4"/>`;

    // Eingang von L1
    svg += `<line x1="${x + 15}" y1="90" x2="${x + 15}" y2="${y}" stroke="#000" stroke-width="0.6"/>`;
    svg += `<circle cx="${x + 15}" cy="90" r="2" fill="#000"/>`;

    return svg;
  };

  // ================================================================
  // STROMKREIS-SEITEN - RCDs, LS, RCBOs
  // Layout: Beginnt in Spalte 0 (nicht 1), Spalte 7 nur für Seitenverweise
  // ================================================================
  const renderCircuitsPage = (pageData) => {
    let svg = '';
    const items = pageData.items || [];

    // Seitenverweis am linken Rand - zeigt Herkunft von Seite 2 (Einspeisung)
    svg += `<g transform="translate(${PAGE.drawingLeft + 15}, ${Y.busbars - 10})">
      <circle cx="0" cy="0" r="8" fill="none" stroke="#000" stroke-width="0.5"/>
      <text x="0" y="3" font-size="7" font-family="sans-serif" text-anchor="middle">←</text>
      <text x="0" y="15" font-size="5" font-family="sans-serif" text-anchor="middle">/2</text>
      <text x="0" y="22" font-size="4" font-family="sans-serif" text-anchor="middle" fill="#666">v. Zähler</text>
    </g>`;

    // Sammelschienen - beginnen weiter links
    svg += renderMainBusbars(PAGE.drawingLeft + 40, PAGE.drawingRight - 80);
    svg += renderPEBusbar(PAGE.drawingLeft + 40, PAGE.drawingRight - 80);

    let columnIdx = 0;  // Start bei Spalte 0 statt 1
    let phaseCounter = 0; // Für Phasen-Rotation bei 1-poligen LS

    items.forEach(item => {
      if (item.type === 'rcd_group') {
        const rcdX = getColumnX(columnIdx) + 10;
        
        // RCD Symbol
        svg += renderRCDSymbol(rcdX, item.rcd, item.isContinuation, item.partNum, item.totalParts);

        // RCD-Ausgangsschienen
        const lastChildCol = columnIdx + (item.children?.length || 0);
        const rcdOutputEndX = getColumnX(lastChildCol) + 30;
        svg += renderRCDOutputBusbars(rcdX, rcdOutputEndX, item.rcd.bezeichnung);

        columnIdx++;

        // LS unter dem RCD
        (item.children || []).forEach(child => {
          const lsX = getColumnX(columnIdx) + 10;
          const is3Pole = child.data?.pole === '3' || child.data?.pole === '3+N';
          
          let assignedPhase = 0;
          if (!is3Pole) {
            assignedPhase = phaseCounter % 3;
            phaseCounter++;
          }

          svg += renderLSSymbol(lsX, child, assignedPhase, is3Pole);
          columnIdx++;
        });

      } else if (item.type === 'rcbo') {
        const x = getColumnX(columnIdx) + 10;
        svg += renderRCBOSymbol(x, item.data);
        columnIdx++;

      } else if (item.type === 'standalone_ls') {
        const x = getColumnX(columnIdx) + 10;
        const is3Pole = item.data?.data?.pole === '3' || item.data?.data?.pole === '3+N';
        
        let assignedPhase = 0;
        if (!is3Pole) {
          assignedPhase = phaseCounter % 3;
          phaseCounter++;
        }
        
        svg += renderStandaloneLSSymbol(x, item.data, assignedPhase, is3Pole);
        columnIdx++;
      }
    });

    // Seitenverweis am rechten Rand (Spalte 7/8)
    svg += `<g transform="translate(${PAGE.drawingRight - 60}, 300)">
      <circle cx="0" cy="0" r="8" fill="none" stroke="#000" stroke-width="0.5"/>
      <text x="0" y="3" font-size="7" font-family="sans-serif" text-anchor="middle">→</text>
      <text x="0" y="15" font-size="5" font-family="sans-serif" text-anchor="middle">/${currentPage + 1}</text>
    </g>`;

    return svg;
  };

  // RCD Symbol (4-polig)
  const renderRCDSymbol = (x, rcd, isContinuation, partNum, totalParts) => {
    const y = Y.firstRow;
    const d = rcd.data || {};
    const bezeichnung = rcd.bezeichnung || 'F1';
    const fNum = bezeichnung.split(' ')[0];
    const fName = bezeichnung.split(' - ')[1] || '';
    const nameLines = wrapText(fName, 12);

    // Klickbare Gruppe mit data-node-id
    let svg = `<g data-node-id="${rcd.id}" class="clickable-component">`;

    // Beschriftung
    svg += `<text x="${x - 3}" y="${y + 10}" text-anchor="end" font-size="8" font-family="sans-serif">-${fNum}</text>`;
    if (isContinuation) {
      svg += `<text x="${x - 3}" y="${y + 18}" text-anchor="end" font-size="6" font-family="sans-serif">(Teil ${partNum}/${totalParts})</text>`;
    } else {
      svg += `<text x="${x - 3}" y="${y + 18}" text-anchor="end" font-size="6.5" font-family="sans-serif">${d.nennstrom || '63'}A ${d.fehlerstrom || '30'}mA</text>`;
    }
    nameLines.forEach((line, i) => {
      svg += `<text x="${x - 3}" y="${y + 24 + i * 6}" text-anchor="end" font-size="6.5" font-family="sans-serif">${line}</text>`;
    });

    // RCD-Symbol (4-polig nach DIN VDE)
    svg += `<g transform="translate(${x},${y})">
      <!-- Pole L1, L2, L3 -->
      ${[0, 15, 30].map(px => `
        <path fill="none" stroke="#000" stroke-width="0.6" d="M${px} 0v5"/>
        <path fill="none" stroke="#000" stroke-width="0.6" d="M${px} 23.892v1.181"/>
        <path fill="none" stroke="#000" stroke-width="0.6" d="m${px + 0.052} 24.04-5.26-19.227"/>
        ${px < 30 ? `<path fill="none" stroke="#000" stroke-width="0.6" stroke-dasharray="1.8,0.6" d="M${px - 2.375} 14.546h11.364"/>` : ''}
      `).join('')}
      <!-- Differenzstrom-Auslöser -->
      <g transform="translate(-20,0) rotate(15 29.557 21.101)">
        <path fill="none" stroke="#000" stroke-width="0.6" d="m15.571 17.01-5.955 3.54"/>
        <path fill="none" stroke="#000" stroke-width="0.6" d="m6.34 19.1 3.08 5.166"/>
        <path fill="none" stroke="#000" stroke-width="0.6" d="m7.655 21.69-3.368 2.082"/>
      </g>
      <!-- N-Pol (blau) -->
      <path fill="none" stroke="${COLORS.N}" stroke-width="0.6" d="M45 0v5"/>
      <path fill="none" stroke="${COLORS.N}" stroke-width="0.6" d="M45 23.892v1.181"/>
      <path fill="none" stroke="${COLORS.N}" stroke-width="0.6" d="M45.053 24.04 40.057 4.974"/>
      <path fill="none" stroke="#000" stroke-width="0.6" stroke-dasharray="1.8,0.6" d="M27.625 14.546h11.364"/>
    </g>`;

    // Zuleitungen von Sammelschienen zum RCD - mit 90° Ecken
    for (let i = 0; i < 4; i++) {
      const busY = Y.busbars + i * Y.busbarSpacing;
      const px = x + i * 15;
      const color = i < 3 ? '#000' : COLORS.N;
      const dashed = i === 3;
      
      svg += `<line x1="${px}" y1="${busY}" x2="${px}" y2="${y}" stroke="${color}" stroke-width="0.6" ${dashed ? 'stroke-dasharray="4,4"' : ''}/>`;
      svg += `<circle cx="${px}" cy="${busY}" r="2" fill="${color}"/>`;
    }

    // Ausgänge von RCD zu Ausgangsschienen
    for (let i = 0; i < 4; i++) {
      const px = x + i * 15;
      const targetY = Y.rcdOutput + i * Y.rcdOutputSpacing;
      const color = i < 3 ? '#000' : COLORS.N;
      const dashed = i === 3;
      svg += `<line x1="${px}" y1="${y + 25}" x2="${px}" y2="${targetY}" stroke="${color}" stroke-width="0.6" ${dashed ? 'stroke-dasharray="4,4"' : ''}/>`;
    }

    // Gruppe schließen
    svg += `</g>`;

    return svg;
  };

  // RCD-Ausgangsschienen
  const renderRCDOutputBusbars = (startX, endX, rcdBezeichnung) => {
    const fNum = (rcdBezeichnung || 'F1').split(' ')[0];
    const busbars = [
      { label: `L1 v. ${fNum}`, y: Y.rcdOutput, color: COLORS.L1, dashed: false },
      { label: `L2 v. ${fNum}`, y: Y.rcdOutput + Y.rcdOutputSpacing, color: COLORS.L2, dashed: false },
      { label: `L3 v. ${fNum}`, y: Y.rcdOutput + 2 * Y.rcdOutputSpacing, color: COLORS.L3, dashed: false },
      { label: `N v. ${fNum}`, y: Y.rcdOutput + 3 * Y.rcdOutputSpacing, color: COLORS.N, dashed: true }
    ];

    return busbars.map((b, i) => `
      <line x1="${startX + i * 8}" y1="${b.y}" x2="${endX}" y2="${b.y}" stroke="${b.color}" stroke-width="0.8" ${b.dashed ? 'stroke-dasharray="5,2"' : ''}/>
      <text x="${endX + 5}" y="${b.y + 3}" font-size="6" font-family="Arial" fill="${b.color}">${b.label}</text>
    `).join('');
  };

  // LS Symbol (unter RCD)
  const renderLSSymbol = (x, ls, assignedPhase, is3Pole) => {
    const y = Y.secondRow;
    const d = ls.data || {};
    const bezeichnung = ls.bezeichnung || 'F1.1';
    const fNum = bezeichnung.split(' ')[0];
    const verbraucher = d.verbraucher || bezeichnung.split(' - ')[1] || '';
    const verbraucherLines = wrapText(verbraucher, 12);
    const kabel = getKabel(ls);
    const hasKabel = kabel.length > 0;

    // Klickbare Gruppe mit data-node-id
    let svg = `<g data-node-id="${ls.id}" class="clickable-component">`;

    // Beschriftung
    svg += `<text x="${x - 5}" y="${y + 10}" text-anchor="end" font-size="8" font-weight="bold" font-family="Arial">-${fNum}</text>`;
    svg += `<text x="${x - 5}" y="${y + 20}" text-anchor="end" font-size="7" font-family="Arial">${d.charakteristik || 'B'}${d.nennstrom || '16'}A</text>`;
    verbraucherLines.forEach((line, i) => {
      svg += `<text x="${x - 5}" y="${y + 30 + i * 9}" text-anchor="end" font-size="6" font-family="Arial">${line}</text>`;
    });

    if (is3Pole) {
      // 3-polig
      const poleSpacing = 8;
      for (let i = 0; i < 3; i++) {
        const px = x + i * poleSpacing;
        const busY = Y.rcdOutput + i * Y.rcdOutputSpacing;

        // Verbindung zur RCD-Ausgangsschiene - 90° Ecke
        svg += `<line x1="${px}" y1="${busY}" x2="${px}" y2="${y}" stroke="#000" stroke-width="0.6"/>`;
        svg += `<circle cx="${px}" cy="${busY}" r="2" fill="#000"/>`;

        // Kontakte
        svg += `<circle cx="${px}" cy="${y}" r="1.2" fill="none" stroke="#000" stroke-width="0.4"/>`;
        svg += `<line x1="${px}" y1="${y + 2}" x2="${px + 5}" y2="${y + 22}" stroke="#000" stroke-width="0.6"/>`;
        svg += `<circle cx="${px}" cy="${y + 28}" r="1.2" fill="#000"/>`;
        svg += `<line x1="${px}" y1="${y + 30}" x2="${px}" y2="${y + 50}" stroke="#000" stroke-width="0.6"/>`;
      }

      // Mechanische Kopplung
      svg += `<line x1="${x}" y1="${y + 12}" x2="${x + 2 * poleSpacing}" y2="${y + 12}" stroke="#000" stroke-width="0.6" stroke-dasharray="2,1"/>`;

      // Auslösesymbol (einmal)
      svg += `<line x1="${x + 3}" y1="${y + 6}" x2="${x + 18}" y2="${y + 6}" stroke="#000" stroke-width="0.6"/>`;
      svg += `<polygon points="${x + 18},${y + 6} ${x + 15},${y + 4} ${x + 15},${y + 8}" fill="#000"/>`;
      svg += `<path d="M ${x + 20} ${y + 2} L ${x + 20} ${y + 5} L ${x + 23} ${y + 5} L ${x + 23} ${y + 8} L ${x + 26} ${y + 8} L ${x + 26} ${y + 11}" fill="none" stroke="#000" stroke-width="0.6"/>`;

      // N-Verbindung
      const nBusY = Y.rcdOutput + 3 * Y.rcdOutputSpacing;
      svg += `<line x1="${x + 28}" y1="${nBusY}" x2="${x + 28}" y2="${y + 50}" stroke="${COLORS.N}" stroke-width="0.6" stroke-dasharray="3,2"/>`;
      svg += `<circle cx="${x + 28}" cy="${nBusY}" r="1.5" fill="${COLORS.N}"/>`;

      // Kabel/Klemmen
      if (hasKabel) {
        svg += renderTerminals(x, kabel, true);
      }

    } else {
      // 1-polig mit Phasen-Rotation
      const busY = Y.rcdOutput + assignedPhase * Y.rcdOutputSpacing;

      // Verbindung zur RCD-Ausgangsschiene - 90° Ecke
      svg += `<line x1="${x}" y1="${busY}" x2="${x}" y2="${y}" stroke="#000" stroke-width="0.6"/>`;
      svg += `<circle cx="${x}" cy="${busY}" r="2" fill="#000"/>`;

      // Kontakte
      svg += `<circle cx="${x}" cy="${y}" r="1.2" fill="none" stroke="#000" stroke-width="0.4"/>`;
      svg += `<line x1="${x}" y1="${y + 2}" x2="${x + 5}" y2="${y + 22}" stroke="#000" stroke-width="0.6"/>`;

      // Auslösesymbol
      svg += `<line x1="${x + 2}" y1="${y + 6}" x2="${x + 15}" y2="${y + 6}" stroke="#000" stroke-width="0.4"/>`;
      svg += `<polygon points="${x + 15},${y + 6} ${x + 12},${y + 4} ${x + 12},${y + 8}" fill="#000"/>`;
      svg += `<path d="M ${x + 17} ${y + 2} L ${x + 17} ${y + 5} L ${x + 20} ${y + 5} L ${x + 20} ${y + 8} L ${x + 23} ${y + 8} L ${x + 23} ${y + 11}" fill="none" stroke="#000" stroke-width="0.5"/>`;

      svg += `<circle cx="${x}" cy="${y + 28}" r="1.2" fill="#000"/>`;
      svg += `<line x1="${x}" y1="${y + 30}" x2="${x}" y2="${y + 50}" stroke="#000" stroke-width="0.6"/>`;

      // N-Verbindung
      const nBusY = Y.rcdOutput + 3 * Y.rcdOutputSpacing;
      svg += `<line x1="${x + 15}" y1="${nBusY}" x2="${x + 15}" y2="${y + 50}" stroke="${COLORS.N}" stroke-width="0.6" stroke-dasharray="3,2"/>`;
      svg += `<circle cx="${x + 15}" cy="${nBusY}" r="2" fill="${COLORS.N}"/>`;

      // Kabel/Klemmen
      if (hasKabel) {
        svg += renderTerminals(x, kabel, false);
      }
    }

    // Gruppe schließen
    svg += `</g>`;

    return svg;
  };

  // RCBO Symbol (FI/LS Kombination)
  const renderRCBOSymbol = (x, rcbo) => {
    const y = Y.firstRow;
    const d = rcbo.data || {};
    const bezeichnung = rcbo.bezeichnung || 'F0.1';
    const fNum = bezeichnung.split(' ')[0];
    const verbraucher = d.verbraucher || bezeichnung.split(' - ')[1] || '';
    const verbraucherLines = wrapText(verbraucher, 12);

    // Klickbare Gruppe mit data-node-id
    let svg = `<g data-node-id="${rcbo.id}" class="clickable-component">`;

    // Beschriftung
    svg += `<text x="${x - 5}" y="${y + 10}" text-anchor="end" font-size="8" font-weight="bold" font-family="Arial">-${fNum}</text>`;
    svg += `<text x="${x - 5}" y="${y + 20}" text-anchor="end" font-size="7" font-family="Arial">${d.charakteristik || 'B'}${d.nennstrom || '16'}A</text>`;
    svg += `<text x="${x - 5}" y="${y + 30}" text-anchor="end" font-size="6" font-family="Arial">${d.fehlerstrom || '30'}mA</text>`;
    verbraucherLines.forEach((line, i) => {
      svg += `<text x="${x - 5}" y="${y + 40 + i * 9}" text-anchor="end" font-size="5" font-family="Arial">${line}</text>`;
    });

    // Zuleitungen von Sammelschienen
    svg += `<line x1="${x}" y1="${Y.busbars}" x2="${x}" y2="${y}" stroke="#000" stroke-width="0.6"/>`;
    svg += `<circle cx="${x}" cy="${Y.busbars}" r="2" fill="#000"/>`;
    svg += `<line x1="${x + 15}" y1="${Y.busbars + 3 * Y.busbarSpacing}" x2="${x + 15}" y2="${y}" stroke="${COLORS.N}" stroke-width="0.6" stroke-dasharray="3,2"/>`;
    svg += `<circle cx="${x + 15}" cy="${Y.busbars + 3 * Y.busbarSpacing}" r="2" fill="${COLORS.N}"/>`;

    // Kontakte
    svg += `<circle cx="${x}" cy="${y}" r="1.2" fill="none" stroke="#000" stroke-width="0.4"/>`;
    svg += `<line x1="${x}" y1="${y + 2}" x2="${x + 5}" y2="${y + 20}" stroke="#000" stroke-width="0.6"/>`;

    // Auslösesymbol
    svg += `<line x1="${x + 2}" y1="${y + 6}" x2="${x + 15}" y2="${y + 6}" stroke="#000" stroke-width="0.4"/>`;
    svg += `<polygon points="${x + 15},${y + 6} ${x + 12},${y + 4} ${x + 12},${y + 8}" fill="#000"/>`;
    svg += `<path d="M ${x + 17} ${y + 2} L ${x + 17} ${y + 5} L ${x + 20} ${y + 5} L ${x + 20} ${y + 8} L ${x + 23} ${y + 8} L ${x + 23} ${y + 11}" fill="none" stroke="#000" stroke-width="0.5"/>`;

    svg += `<circle cx="${x}" cy="${y + 26}" r="1.2" fill="#000"/>`;
    svg += `<line x1="${x}" y1="${y + 28}" x2="${x}" y2="${y + 38}" stroke="#000" stroke-width="0.5"/>`;

    // FI-Teil (Summenstromwandler)
    const stX = x + 24;
    const stY = y + 30;
    svg += `<rect x="${stX}" y="${stY}" width="12" height="10" fill="none" stroke="#000" stroke-width="0.4"/>`;
    svg += `<line x1="${stX}" y1="${stY}" x2="${stX + 12}" y2="${stY + 10}" stroke="#000" stroke-width="0.3"/>`;
    svg += `<line x1="${stX + 12}" y1="${stY}" x2="${stX}" y2="${stY + 10}" stroke="#000" stroke-width="0.3"/>`;

    // Ausgänge
    svg += `<line x1="${x}" y1="${y + 38}" x2="${x}" y2="${Y.terminals}" stroke="#000" stroke-width="0.4"/>`;
    svg += `<line x1="${x + 15}" y1="${y}" x2="${x + 15}" y2="${Y.terminals}" stroke="${COLORS.N}" stroke-width="0.4" stroke-dasharray="3,2"/>`;
    svg += `<line x1="${x + 25}" y1="${Y.peBusbar}" x2="${x + 25}" y2="${Y.terminals}" stroke="${COLORS.PE}" stroke-width="0.4" stroke-dasharray="3,2"/>`;
    svg += `<circle cx="${x + 25}" cy="${Y.peBusbar}" r="1.5" fill="${COLORS.PE}"/>`;

    return svg;
  };

  // Standalone LS Symbol (direkt an Sammelschienen)
  const renderStandaloneLSSymbol = (x, ls, assignedPhase, is3Pole) => {
    const y = Y.firstRow;
    const d = ls.data || {};
    const bezeichnung = ls.bezeichnung || 'F0.1';
    const fNum = bezeichnung.split(' ')[0];
    const verbraucher = d.verbraucher || bezeichnung.split(' - ')[1] || '';
    const verbraucherLines = wrapText(verbraucher, 10);
    const kabel = getKabel(ls);
    const hasKabel = kabel.length > 0;

    let svg = '';

    // Beschriftung
    svg += `<text x="${x - 5}" y="${y + 10}" text-anchor="end" font-size="8" font-weight="bold" font-family="Arial">-${fNum}</text>`;
    svg += `<text x="${x - 5}" y="${y + 20}" text-anchor="end" font-size="7" font-family="Arial">${d.charakteristik || 'B'}${d.nennstrom || '16'}A</text>`;
    verbraucherLines.forEach((line, i) => {
      svg += `<text x="${x - 5}" y="${y + 30 + i * 8}" text-anchor="end" font-size="5" font-family="Arial">${line}</text>`;
    });

    // Zuleitung von Sammelschiene
    const busY = Y.busbars + assignedPhase * Y.busbarSpacing;
    svg += `<line x1="${x}" y1="${busY}" x2="${x}" y2="${y}" stroke="#000" stroke-width="0.6"/>`;
    svg += `<circle cx="${x}" cy="${busY}" r="2" fill="#000"/>`;

    // Kontakte
    svg += `<circle cx="${x}" cy="${y}" r="1.2" fill="none" stroke="#000" stroke-width="0.4"/>`;
    svg += `<line x1="${x}" y1="${y + 2}" x2="${x + 5}" y2="${y + 22}" stroke="#000" stroke-width="0.6"/>`;

    // Auslösesymbol
    svg += `<line x1="${x + 2}" y1="${y + 8}" x2="${x + 15}" y2="${y + 8}" stroke="#000" stroke-width="0.4"/>`;
    svg += `<polygon points="${x + 15},${y + 8} ${x + 12},${y + 6} ${x + 12},${y + 10}" fill="#000"/>`;
    svg += `<path d="M ${x + 17} ${y + 4} L ${x + 17} ${y + 7} L ${x + 20} ${y + 7} L ${x + 20} ${y + 10} L ${x + 23} ${y + 10} L ${x + 23} ${y + 13}" fill="none" stroke="#000" stroke-width="0.5"/>`;

    svg += `<circle cx="${x}" cy="${y + 28}" r="1.2" fill="#000"/>`;
    svg += `<line x1="${x}" y1="${y + 30}" x2="${x}" y2="${y + 70}" stroke="#000" stroke-width="0.6"/>`;

    // N-Verbindung
    const nBusY = Y.busbars + 3 * Y.busbarSpacing;
    svg += `<line x1="${x + 15}" y1="${nBusY}" x2="${x + 15}" y2="${y + 70}" stroke="${COLORS.N}" stroke-width="0.6" stroke-dasharray="3,2"/>`;
    svg += `<circle cx="${x + 15}" cy="${nBusY}" r="2" fill="${COLORS.N}"/>`;

    // Kabel/Klemmen
    if (hasKabel) {
      svg += `<line x1="${x}" y1="${y + 70}" x2="${x}" y2="${Y.terminals}" stroke="#000" stroke-width="0.6"/>`;
      svg += `<line x1="${x + 15}" y1="${y + 70}" x2="${x + 15}" y2="${Y.terminals}" stroke="${COLORS.N}" stroke-width="0.6" stroke-dasharray="4,4"/>`;
      svg += `<line x1="${x + 25}" y1="${Y.peBusbar}" x2="${x + 25}" y2="${Y.terminals}" stroke="${COLORS.PE}" stroke-width="0.6" stroke-dasharray="3,2"/>`;
      svg += `<circle cx="${x + 25}" cy="${Y.peBusbar}" r="2" fill="${COLORS.PE}"/>`;
      svg += renderTerminals(x, kabel, false);
    }

    // Gruppe schließen
    svg += `</g>`;

    return svg;
  };

  // Klemmenblock
  const renderTerminals = (x, kabelList, is3Pole) => {
    const y = Y.terminals;
    let svg = '';

    if (!kabelList || kabelList.length === 0) return svg;

    kabelList.forEach((kabel, idx) => {
      const kd = kabel.data || {};
      const offsetX = idx * 50;
      const klemmleiste = kd.klemmleiste || 'X1';
      const kabeltyp = formatKabeltyp(kd);
      const labels = is3Pole ? ['1', '2', '3', 'N', 'PE'] : ['1', 'N', 'PE'];
      const spacing = 7;

      svg += `<text x="${x + offsetX - 6}" y="${y + 10}" font-size="5" font-family="Arial">-${klemmleiste}</text>`;

      labels.forEach((label, i) => {
        const tx = x + offsetX + i * spacing;
        const isN = label === 'N';
        const isPE = label === 'PE';

        svg += `<rect x="${tx}" y="${y}" width="6" height="14" fill="${isPE ? '#90EE90' : isN ? '#ADD8E6' : '#FFF'}" stroke="#000" stroke-width="0.3"/>`;
        svg += `<text x="${tx + 3}" y="${y + 9}" text-anchor="middle" font-size="4" font-weight="bold" font-family="Arial">${label}</text>`;
      });

      // Kabeltyp
      svg += `<text x="${x + offsetX + (labels.length * spacing) / 2}" y="${y + 28}" text-anchor="middle" font-size="4" font-family="Arial">${kabeltyp}</text>`;
      
      // Kabelname
      if (kabel.bezeichnung) {
        svg += `<text x="${x + offsetX - 12}" y="${y + 35}" text-anchor="start" font-size="5" font-family="Arial" transform="rotate(90, ${x + offsetX - 12}, ${y + 35})">${kabel.bezeichnung}</text>`;
      }
    });

    return svg;
  };

  // ================================================================
  // SEITEN-RENDERING
  // ================================================================
  const renderCurrentPage = () => {
    const page = projectData.pages[currentPage - 1];
    if (!page) return '';

    let svg = '';

    switch (page.type) {
      case 'cover':
        svg += renderCoverPage();
        break;
      case 'einspeisung':
        svg += renderEinspeisungPage(page);
        break;
      case 'circuits':
        svg += renderCircuitsPage(page);
        break;
      default:
        svg += `<text x="${PAGE.width/2}" y="${PAGE.height/2}" text-anchor="middle" font-size="14" fill="#999">Unbekannter Seitentyp</text>`;
    }

    return svg;
  };

  // ================================================================
  // EXPORT FUNKTIONEN
  // ================================================================
  const handleExportSVG = useCallback(() => {
    if (!svgRef.current) return;
    const svgData = svgRef.current.outerHTML;
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stromlaufplan_seite_${currentPage}.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [currentPage]);

  const handlePrint = useCallback(() => {
    const printWindow = window.open('', '_blank');
    if (!printWindow || !svgRef.current) return;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Stromlaufplan - Seite ${currentPage}</title>
        <style>
          @page { size: A4 landscape; margin: 0; }
          body { margin: 0; display: flex; justify-content: center; align-items: center; }
          svg { width: 297mm; height: 210mm; }
        </style>
      </head>
      <body>${svgRef.current.outerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  }, [currentPage]);

  // ================================================================
  // RENDER
  // ================================================================
  
  // Doppelklick-Handler für Zoom
  const handleDoubleClick = useCallback((e) => {
    // Toggle zwischen 100% und 150%
    setZoom(prev => prev >= 1.4 ? 1 : 1.5);
  }, []);

  return (
    <div className={`flex flex-col h-full bg-gray-100 ${compact ? '' : ''}`}>
      {/* Toolbar */}
      <div className={`flex items-center justify-between ${compact ? 'px-2 py-1' : 'px-4 py-2'} bg-white border-b border-gray-200`}>
        <div className="flex items-center gap-2">
          <span className={`font-medium text-gray-700 ${compact ? 'text-xs' : 'text-sm'}`}>
            Seite {currentPage} / {totalPages}
          </span>
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              className={compact ? 'h-6 w-6 p-0' : ''}
            >
              <ChevronLeft className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className={compact ? 'h-6 w-6 p-0' : ''}
            >
              <ChevronRight className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {/* Werkzeug-Umschalter */}
          <div className="flex items-center border rounded-md overflow-hidden mr-2">
            <Button 
              variant={toolMode === 'select' ? 'default' : 'ghost'} 
              size="sm" 
              onClick={() => setToolMode('select')}
              className={`${compact ? 'h-6 w-6 p-0' : 'h-8 px-2'} rounded-none ${toolMode === 'select' ? 'bg-emerald-600 text-white hover:bg-emerald-700' : ''}`}
              title="Auswählen (Klick auf Symbole)"
            >
              <MousePointer2 className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
            </Button>
            <Button 
              variant={toolMode === 'pan' ? 'default' : 'ghost'} 
              size="sm" 
              onClick={() => setToolMode('pan')}
              className={`${compact ? 'h-6 w-6 p-0' : 'h-8 px-2'} rounded-none border-l ${toolMode === 'pan' ? 'bg-emerald-600 text-white hover:bg-emerald-700' : ''}`}
              title="Verschieben (Ziehen zum Scrollen)"
            >
              <Hand className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
            </Button>
            <Button 
              variant={toolMode === 'zoom' ? 'default' : 'ghost'} 
              size="sm" 
              onClick={() => setToolMode('zoom')}
              className={`${compact ? 'h-6 w-6 p-0' : 'h-8 px-2'} rounded-none border-l ${toolMode === 'zoom' ? 'bg-emerald-600 text-white hover:bg-emerald-700' : ''}`}
              title="Zoom (Mausrad zum Zoomen)"
            >
              <Search className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
            </Button>
          </div>
          
          <div className="w-px h-6 bg-gray-300 mx-1" />
          
          <Button variant="ghost" size="sm" onClick={() => setZoom(z => Math.max(0.3, z - 0.15))} className={compact ? 'h-6 w-6 p-0' : ''}>
            <ZoomOut className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
          </Button>
          <button 
            className={`${compact ? 'text-xs px-1' : 'text-sm px-2'} py-1 hover:bg-gray-100 rounded text-center min-w-[50px]`}
            onClick={() => setZoom(1)}
            title="Auf 100% zurücksetzen"
          >
            {Math.round(zoom * 100)}%
          </button>
          <Button variant="ghost" size="sm" onClick={() => setZoom(z => Math.min(3, z + 0.15))} className={compact ? 'h-6 w-6 p-0' : ''}>
            <ZoomIn className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
          </Button>
          {/* Export-Buttons immer sichtbar, neben Zoom */}
          <div className="w-px h-6 bg-gray-300 mx-2" />
          <Button variant="outline" size="sm" onClick={handleExportSVG} className={compact ? 'h-6 text-xs px-2' : ''}>
            <Download className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
            {!compact && <span className="ml-1">SVG</span>}
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint} className={compact ? 'h-6 text-xs px-2' : ''}>
            <Printer className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
            {!compact && <span className="ml-1">Drucken</span>}
          </Button>
        </div>
      </div>

      {/* SVG Canvas */}
      <div 
        ref={containerRef}
        className={`flex-1 overflow-auto p-4 ${toolMode === 'pan' ? 'cursor-grab' : ''} ${isPanning ? 'cursor-grabbing' : ''}`}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div className="inline-block min-w-full" style={{ minHeight: '100%' }}>
          <div className="flex justify-center p-4">
            <svg
              ref={svgRef}
              viewBox={`0 0 ${PAGE.width} ${PAGE.height}`}
              width={PAGE.width * zoom}
              height={PAGE.height * zoom}
              className={`bg-white shadow-lg ${toolMode === 'select' ? 'cursor-pointer' : toolMode === 'zoom' ? 'cursor-zoom-in' : ''}`}
              xmlns="http://www.w3.org/2000/svg"
              onDoubleClick={handleDoubleClick}
              onClick={handleSvgClick}
              style={{ cursor: toolMode === 'zoom' ? (zoom >= 1.4 ? 'zoom-out' : 'zoom-in') : undefined }}
            >
              <defs>
                <style>{`
                  text { font-family: Arial, sans-serif; }
                  .clickable-component { cursor: pointer; }
                  .clickable-component:hover { filter: brightness(0.9); }
                `}</style>
              </defs>
              <rect width={PAGE.width} height={PAGE.height} fill="white"/>
              <g dangerouslySetInnerHTML={{ __html: renderPageFrame() + renderTitleBlock() + renderCurrentPage() }} />
            </svg>
          </div>
        </div>
      </div>
      
      {/* Zoom Hint */}
      {!compact && (
        <div className="text-center text-xs text-gray-400 py-1 bg-white border-t">
          {toolMode === 'select' && 'Klicken Sie auf Symbole, um sie auszuwählen'}
          {toolMode === 'pan' && 'Ziehen Sie, um den Plan zu verschieben'}
          {toolMode === 'zoom' && 'Mausrad zum Zoomen | Doppelklick für 100% ↔ 150%'}
        </div>
      )}
    </div>
  );
};

export default StromlaufplanView;
